import { orderBy } from 'lodash-es'
import { shuffle } from 'lodash-es'
import { generatePath } from 'react-router'
import { PROBLEM_SORT_FILTER } from 'src/constant'
import commonRepo from 'src/repository/common.repo'

import type { GroupInfo, Student } from '@/dto'
import { CommonError, MakeProblem } from '@/dto'
import type { RawMakeProblemT } from '@/page/MakeCustomWorkbook/context/abstract/baseMakeCustomWorkbookContext'
import { RouteName } from '@/router'

// 프로젝트 관련 유틸들
export const mathflatUtils = {
  // 수업 - 학습지로 가는 경로 groupInfo 기반 generate
  generateLessonWorksheetPath(groupInfo: GroupInfo) {
    switch (groupInfo.data.type) {
      case 'grade':
        return generatePath(RouteName.lessonWorksheetGrade, {
          gradeKey: String(groupInfo.key),
        }) as (typeof RouteName)['lessonWorksheetGrade']
      case 'lesson-class':
        return generatePath(RouteName.lessonWorksheetClass, {
          lessonClassId: String(groupInfo.key),
        }) as (typeof RouteName)['lessonWorksheetClass']
      case 'student':
        return generatePath(RouteName.lessonWorksheetStudent, {
          studentId: String(groupInfo.key),
        }) as (typeof RouteName)['lessonWorksheetStudent']
    }
  },

  generateReportPath(groupType: GroupInfo['data']['type'], groupKey: string) {
    switch (groupType) {
      case 'grade':
        return generatePath(RouteName.lessonReportGrade, { gradeKey: String(groupKey) })
      case 'lesson-class':
        return generatePath(RouteName.lessonReportClass, { lessonClassId: String(groupKey) })
      case 'student':
        return generatePath(RouteName.lessonReportStudent, { studentId: String(groupKey) })
      default:
        return ''
    }
  },

  // 오답학습지를 통해 수업 - 학습지로 가는 경로(groupInfo를 사용할 수 없는 경우)
  generateWeakWorksheetPath(groupType: GroupInfo['data']['type'], groupKey: string) {
    switch (groupType) {
      case 'grade':
        return generatePath(RouteName.lessonWorksheetGrade, { gradeKey: String(groupKey) })
      case 'lesson-class':
        return generatePath(RouteName.lessonWorksheetClass, { lessonClassId: String(groupKey) })
      case 'student':
        return generatePath(RouteName.lessonWorkbookStudent, { studentId: String(groupKey) })
      default:
        return ''
    }
  },

  // 유형분석 페이지로 가는 경로
  generateAchievementAnalysisPath(groupType: GroupInfo['data']['type'], groupKey: string) {
    switch (groupType) {
      case 'grade':
        return generatePath(RouteName.achievementAnalysisByGrade, { gradeKey: String(groupKey) })
      case 'lesson-class':
        return generatePath(RouteName.achievementAnalysisClass, { lessonClassId: String(groupKey) })
      case 'student':
        return generatePath(RouteName.achievementAnalysisByStudent, { studentId: String(groupKey) })
      default:
        return ''
    }
  },

  // 문자 보내기 전 예외처리
  validSendSMS<T extends { phone?: Student['phone'] }>(phoneData?: T) {
    if (commonRepo.academy?.status === 'FREE_INPROGRESS') {
      throw new CommonError('문자 전송은 정식 가입 후 이용할 수 있습니다.', { type: 'client' })
    }
    if (!commonRepo.academy?.cellPhone) {
      throw new CommonError('문자관리탭에서 발신번호를 등록해주세요.', { type: 'client' })
    }
    if (phoneData && !phoneData.phone) {
      throw new CommonError('핸드폰 번호를 찾을 수 없습니다.', { type: 'client' })
    }
  },

  problemSortByType: <T extends MakeProblem | RawMakeProblemT>(
    problemSortType: common.MakeWorksheetProblemSortType,
    problemList: T[],
  ) => {
    let koSort = ''
    let sortProblemList: T[] = []

    PROBLEM_SORT_FILTER.forEach(({ label, value }) => {
      if (value === problemSortType) {
        koSort = label
      }
    })

    switch (problemSortType) {
      case 'CONCEPT':
        sortProblemList = orderBy(
          problemList,
          [
            (data) => {
              if (data instanceof MakeProblem) {
                return data.sequence
              }
              return data.concept.orderingNumber
            },
          ],
          'asc',
        )
        koSort = `${koSort}으`
        break
      case 'LEVEL':
        sortProblemList = orderBy(problemList, [(data) => data.level], 'asc')
        koSort = `${koSort}으`
        break
      case 'RANDOM':
        sortProblemList = shuffle(problemList)
        break
      case 'SINGLE_CHOICE':
        sortProblemList = mathflatUtils.sortSingleType(problemList)
        break
      case 'PROBLEM':
        sortProblemList = mathflatUtils.sortProblemIndex(problemList)
        koSort = `${koSort}`
        break
      case 'USER':
        koSort = `${koSort}`
        sortProblemList = problemList
    }

    return { koSort, sortProblemList }
  },
  // 문제순 정렬
  sortProblemIndex: <T extends MakeProblem | RawMakeProblemT>(problemList: T[]) => {
    //우선 순위 1) 동일 문제 원본 문제 순 정렬
    //우선 순위 2) 동일 문제 뒤에 1:1 유사 문제 정렬
    // 그 외에 문제들(Ex - 왼쪽에서 추가된 문제)은 오름차순으로 정렬 뒤 합침
    let initProblems: T[] = []
    let latelyProblems: T[] = []
    problemList.forEach((problem) => {
      if (!(problem instanceof MakeProblem) || problem.defaultOrder !== null) {
        initProblems.push(problem)
      } else {
        latelyProblems.push(problem)
      }
    })

    initProblems = orderBy(initProblems, [
      (data) => {
        if (data instanceof MakeProblem) {
          return data.defaultOrder
        }
        return data.concept.orderingNumber
      },
      'asc',
    ])
    latelyProblems = orderBy(
      latelyProblems,
      [
        (data) => {
          if (data instanceof MakeProblem) {
            return data.conceptId
          }
          return data.concept.id
        },
      ],
      'asc',
    )
    return [...initProblems, ...latelyProblems]
  },

  sortSingleType: <T extends MakeProblem | RawMakeProblemT>(problemList: T[]) => {
    const singleProblemList: T[] = []
    const shortProblemList: T[] = []
    const multipleProblemList: T[] = []
    const essayProblemList: T[] = []

    // 객관식 정렬일경우
    // 객관식 - 선다형 - 주관식 - 그 외 순으로 정렬
    problemList.forEach((problem) => {
      switch (problem instanceof MakeProblem ? problem.type : problem.answerType) {
        case 'SINGLE_CHOICE':
          singleProblemList.push(problem)
          break

        case 'SHORT_ANSWER':
          shortProblemList.push(problem)
          break
        case 'MULTIPLE_CHOICE':
          multipleProblemList.push(problem)
          break
        case 'ESSAY':
        default:
          essayProblemList.push(problem)
          return
      }
    })

    return [...singleProblemList, ...multipleProblemList, ...shortProblemList, ...essayProblemList]
  },
}
